import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import {
    createInvestment,
    updateInvestment,
    getInvestmentDetails,
    calculateInvestmentPreview,
    clearInvestmentPreview,
} from '../../../../redux/actions/SocietyPortal/InvestmentAction/InvestmentAction';
import { getGLAccounts } from '../../../../redux/actions/SocietyPortal/GLAccountAction/GLAccountAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { toast } from 'react-toastify';
import { INVESTMENT_TYPES, INTEREST_BEARING, MARKET_LINKED, numberLabel } from './investmentTypes';

const INTEREST_MODES = [
    { value: 'simple', label: 'Simple' },
    { value: 'compound', label: 'Compound' },
];

const FREQUENCIES = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'half_yearly', label: 'Half-Yearly' },
    { value: 'annual', label: 'Annual' },
    { value: 'bullet', label: 'Bullet' },
    { value: 'quarter_end', label: 'Quarter End' },
    { value: 'month_end', label: 'Month End' },
];

const todayISO = () => localDateString();

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const inputClass = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500';
const labelClass = 'tw-block tw-text-[11px] tw-font-medium tw-text-gray-700 tw-mb-1';
const req = <span className="tw-text-red-500">*</span>;

const InvestmentModal = ({ editId, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { glAccountsData, loading: glAccountsLoading } = useSelector(state => state.GLAccountReducer || {});
    const { createLoading, updateLoading, detailsLoading, details, preview, previewLoading } = useSelector(state => state.InvestmentReducer || {});
    const saving = createLoading || updateLoading;

    const [form, setForm] = useState({
        investment_type: 'FIXED_DEPOSIT',
        institution_name: '',
        branch_name: '',
        holder_name: '',
        nominee_name: '',
        auto_renew: false,
        lien_marked: false,
        lien_details: '',
        units: '',
        current_value: '',
        current_value_date: '',
        investment_date: todayISO(),
        investment_number: '',
        principal_amount: '',
        interest_rate: '',
        interest_mode: 'simple',
        frequency: 'quarterly',
        maturity_date: '',
        maturity_amount: '',
        debit_account_cd: '',
        credit_account_cd: '',
        interest_account_cd: '',
        interest_collection_bank_cd: '',
        penalty_account_cd: '',
        remarks: '',
    });

    // Fetch GL accounts for dropdowns
    useEffect(() => {
        if (societyId && (!glAccountsData || glAccountsData.length === 0)) {
            dispatch(getGLAccounts({ societyId }));
        }
    }, [dispatch, societyId, glAccountsData]);

    // If editing, load details and populate form
    useEffect(() => {
        if (editId && societyId) {
            dispatch(getInvestmentDetails({ societyId, id: editId }));
        }
    }, [editId, societyId, dispatch]);

    useEffect(() => {
        if (editId && details && details.id === Number(editId)) {
            setForm({
                investment_type: details.investment_type || 'FIXED_DEPOSIT',
                institution_name: details.institution_name || '',
                branch_name: details.branch_name || '',
                holder_name: details.holder_name || '',
                nominee_name: details.nominee_name || '',
                auto_renew: !!details.auto_renew,
                lien_marked: !!details.lien_marked,
                lien_details: details.lien_details || '',
                units: details.units != null ? String(parseFloat(details.units)) : '',
                current_value: details.current_value != null ? String(parseFloat(details.current_value)) : '',
                current_value_date: details.current_value_date?.split('T')[0] || '',
                investment_date: details.investment_date?.split('T')[0] || todayISO(),
                investment_number: details.investment_number || '',
                principal_amount: details.principal_amount ? String(parseFloat(details.principal_amount)) : '',
                interest_rate: details.interest_rate != null ? String(parseFloat(details.interest_rate)) : '',
                interest_mode: details.interest_mode || 'simple',
                frequency: details.frequency || 'quarterly',
                maturity_date: details.maturity_date?.split('T')[0] || '',
                maturity_amount: details.maturity_amount ? String(parseFloat(details.maturity_amount)) : '',
                debit_account_cd: details.debit_account_cd || '',
                credit_account_cd: details.credit_account_cd || '',
                interest_account_cd: details.interest_account_cd || '',
                interest_collection_bank_cd: details.interest_collection_bank_cd || '',
                penalty_account_cd: details.penalty_account_cd || '',
                remarks: details.remarks || '',
            });
        }
    }, [editId, details]);

    const accountOptions = useMemo(() => {
        if (!glAccountsData) return [];
        return (glAccountsData || []).map(acc => ({
            value: acc.ac_code,
            label: `${acc.ac_code} - ${acc.name}`,
        }));
    }, [glAccountsData]);

    // Debounced auto-preview: only fires when all required preview fields are valid
    useEffect(() => {
        const { investment_date, maturity_date, principal_amount, interest_rate, interest_mode, frequency } = form;
        const principal = parseFloat(principal_amount);
        const rate = parseFloat(interest_rate);

        if (!investment_date || !maturity_date || !interest_mode || !frequency) {
            dispatch(clearInvestmentPreview());
            return;
        }
        if (!principal || principal <= 0 || isNaN(rate) || rate < 0) {
            dispatch(clearInvestmentPreview());
            return;
        }
        if (new Date(maturity_date) <= new Date(investment_date)) {
            dispatch(clearInvestmentPreview());
            return;
        }

        const timer = setTimeout(() => {
            dispatch(calculateInvestmentPreview({
                investment_date,
                maturity_date,
                principal_amount: principal,
                interest_rate: rate,
                interest_mode,
                frequency,
            }));
        }, 400);

        return () => clearTimeout(timer);
    }, [
        dispatch,
        form.investment_date,
        form.maturity_date,
        form.principal_amount,
        form.interest_rate,
        form.interest_mode,
        form.frequency,
    ]);

    // Clear preview on unmount
    useEffect(() => () => { dispatch(clearInvestmentPreview()); }, [dispatch]);

    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    // Interest-bearing types need rate / mode / frequency / maturity. Market-
    // linked ones (mutual funds, shares) carry units and a current value
    // instead; "Other" may have either.
    const interestRequired = INTEREST_BEARING.includes(form.investment_type);
    const marketLinked = MARKET_LINKED.includes(form.investment_type);
    const showInterest = !marketLinked;
    const hasRate = parseFloat(form.interest_rate) > 0;
    const interestTermsNeeded = interestRequired || hasRate;

    const handleSubmit = (e) => {
        e.preventDefault();
        const labels = {
            investment_date: 'Investment date',
            investment_number: numberLabel(form.investment_type),
            principal_amount: 'Amount invested',
            interest_rate: 'Interest rate',
            maturity_date: 'Maturity date',
            debit_account_cd: 'Investment GL account',
            credit_account_cd: 'Bank account',
        };
        const required = ['investment_date', 'investment_number', 'principal_amount', 'debit_account_cd', 'credit_account_cd'];
        if (interestRequired) required.push('interest_rate');
        if (interestTermsNeeded && showInterest) required.push('maturity_date');
        for (const f of required) {
            if (!form[f] && form[f] !== 0) {
                toast.warn(`${labels[f] || f.replace(/_/g, ' ')} is required`);
                return;
            }
        }
        if (form.maturity_date && form.maturity_date <= form.investment_date) {
            toast.warn('Maturity date must be after the investment date');
            return;
        }

        const sendInterest = showInterest && (interestRequired || hasRate);
        const num = (v) => (v === '' || v == null ? null : parseFloat(v));
        const payload = {
            societyId,
            investment_type: form.investment_type,
            institution_name: form.institution_name.trim() || null,
            branch_name: form.branch_name.trim() || null,
            holder_name: form.holder_name.trim() || null,
            nominee_name: form.nominee_name.trim() || null,
            auto_renew: !!form.auto_renew,
            lien_marked: !!form.lien_marked,
            lien_details: form.lien_marked ? (form.lien_details.trim() || null) : null,
            units: num(form.units),
            current_value: num(form.current_value),
            current_value_date: form.current_value !== '' ? (form.current_value_date || form.investment_date) : null,
            investment_date: form.investment_date,
            investment_number: form.investment_number.trim(),
            principal_amount: parseFloat(form.principal_amount),
            interest_rate: sendInterest ? parseFloat(form.interest_rate) : null,
            interest_mode: sendInterest ? form.interest_mode : null,
            frequency: sendInterest ? form.frequency : null,
            maturity_date: form.maturity_date || null,
            maturity_amount: form.maturity_amount ? parseFloat(form.maturity_amount) : undefined,
            debit_account_cd: form.debit_account_cd,
            credit_account_cd: form.credit_account_cd,
            interest_account_cd: form.interest_account_cd || undefined,
            interest_collection_bank_cd: form.interest_collection_bank_cd || undefined,
            penalty_account_cd: form.penalty_account_cd || undefined,
            remarks: form.remarks || undefined,
            onSuccess: () => onSaved?.(),
        };

        if (editId) {
            dispatch(updateInvestment({ id: editId, ...payload }));
        } else {
            dispatch(createInvestment(payload));
        }
    };

    const loadingDetails = editId && detailsLoading;

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40" onClick={onClose}>
            <div
                className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-3xl tw-mx-4 tw-max-h-[92vh] tw-overflow-hidden tw-flex tw-flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-gradient-to-br tw-from-rose-500 tw-to-pink-600 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="hugeicons:chart-increase" className="tw-text-white tw-text-sm" />
                        </div>
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">{editId ? 'Edit Investment' : 'Add Investment'}</h2>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="tw-flex-1 tw-overflow-y-auto">
                    <div className="tw-p-5 tw-space-y-4">
                        {loadingDetails && (
                            <div className="tw-flex tw-items-center tw-justify-center tw-py-4">
                                <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                                <span className="tw-text-xs tw-text-gray-400 tw-ml-2">Loading details...</span>
                            </div>
                        )}

                        {/* Investment type */}
                        <div>
                            <label className={labelClass}>Investment Type {req}</label>
                            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-2">
                                {INVESTMENT_TYPES.map(t => (
                                    <button
                                        key={t.value}
                                        type="button"
                                        onClick={() => setField('investment_type', t.value)}
                                        className={`tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-2 tw-rounded-md tw-border tw-text-[11px] tw-font-medium tw-text-left tw-transition-colors ${form.investment_type === t.value ? 'tw-border-rose-500 tw-bg-rose-50 tw-text-rose-700' : 'tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50'}`}
                                    >
                                        <Icon icon={t.icon} className="tw-text-sm tw-flex-shrink-0" />
                                        <span className="tw-truncate">{t.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Row 1 */}
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                            <div>
                                <label className={labelClass}>Investment Date {req}</label>
                                <input type="date" value={form.investment_date} onChange={(e) => setField('investment_date', e.target.value)} className={inputClass} required />
                            </div>
                            <div>
                                <label className={labelClass}>{numberLabel(form.investment_type)} {req}</label>
                                <input type="text" value={form.investment_number} onChange={(e) => setField('investment_number', e.target.value)} placeholder={form.investment_type === 'FIXED_DEPOSIT' ? 'FD/2025-26/001' : ''} className={inputClass} required />
                            </div>
                            <div>
                                <label className={labelClass}>Amount Invested (₹) {req}</label>
                                <input type="number" min="0" step="0.01" value={form.principal_amount} onChange={(e) => setField('principal_amount', e.target.value)} className={inputClass} required />
                            </div>
                        </div>

                        {/* Institution & holding */}
                        <div className="tw-pt-3 tw-border-t tw-border-gray-100">
                            <h3 className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-3">Institution &amp; Holding</h3>
                            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                                <div>
                                    <label className={labelClass}>{marketLinked ? 'AMC / Broker' : 'Bank / Institution'}</label>
                                    <input type="text" maxLength={150} value={form.institution_name} onChange={(e) => setField('institution_name', e.target.value)} placeholder={marketLinked ? 'e.g. HDFC Mutual Fund' : 'e.g. State Bank of India'} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Branch</label>
                                    <input type="text" maxLength={150} value={form.branch_name} onChange={(e) => setField('branch_name', e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Held in the name of</label>
                                    <input type="text" maxLength={200} value={form.holder_name} onChange={(e) => setField('holder_name', e.target.value)} placeholder="e.g. Sunrise CHS Ltd." className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Nominee</label>
                                    <input type="text" maxLength={150} value={form.nominee_name} onChange={(e) => setField('nominee_name', e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-mt-3">
                                {!marketLinked && (
                                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                                        <input type="checkbox" checked={form.auto_renew} onChange={(e) => setField('auto_renew', e.target.checked)} />
                                        Auto-renew on maturity
                                    </label>
                                )}
                                <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                                    <input type="checkbox" checked={form.lien_marked} onChange={(e) => setField('lien_marked', e.target.checked)} />
                                    Lien marked / pledged
                                </label>
                            </div>
                            {form.lien_marked && (
                                <div className="tw-mt-2">
                                    <input type="text" maxLength={255} value={form.lien_details} onChange={(e) => setField('lien_details', e.target.value)} placeholder="Lien details — e.g. against overdraft with SBI" className={inputClass} />
                                </div>
                            )}
                        </div>

                        {/* Market value (mutual funds, shares — optional for others) */}
                        {(marketLinked || form.investment_type === 'OTHER') && (
                            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                                <div>
                                    <label className={labelClass}>Units / Quantity</label>
                                    <input type="number" min="0" step="0.0001" value={form.units} onChange={(e) => setField('units', e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Current Value (₹)</label>
                                    <input type="number" min="0" step="0.01" value={form.current_value} onChange={(e) => setField('current_value', e.target.value)} placeholder="Market / NAV value" className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Value as on</label>
                                    <input type="date" value={form.current_value_date} onChange={(e) => setField('current_value_date', e.target.value)} className={inputClass} />
                                </div>
                            </div>
                        )}

                        {showInterest && (
                        <>
                        {!interestRequired && (
                            <p className="tw-text-[11px] tw-text-gray-500">Interest details are optional for this type — fill them only if it earns a fixed rate.</p>
                        )}
                        {/* Row 2 */}
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                            <div>
                                <label className={labelClass}>Interest Rate (%) {interestRequired && req}</label>
                                <input type="number" min="0" step="0.01" value={form.interest_rate} onChange={(e) => setField('interest_rate', e.target.value)} className={inputClass} required={interestRequired} />
                            </div>
                            <div>
                                <label className={labelClass}>Interest Mode {interestTermsNeeded && req}</label>
                                <SearchableDropdown
                                    value={form.interest_mode}
                                    onChange={(v) => setField('interest_mode', v)}
                                    options={INTEREST_MODES}
                                    placeholder="Select Mode"
                                    clearable={false}
                                    small
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Interest Payout Frequency {interestTermsNeeded && req}</label>
                                <SearchableDropdown
                                    value={form.frequency}
                                    onChange={(v) => setField('frequency', v)}
                                    options={FREQUENCIES}
                                    placeholder="Select Frequency"
                                    clearable={false}
                                    small
                                />
                            </div>
                        </div>

                        {/* Row 3 */}
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                            <div>
                                <label className={labelClass}>Maturity Date {interestTermsNeeded && req}</label>
                                <input type="date" value={form.maturity_date} onChange={(e) => setField('maturity_date', e.target.value)} className={inputClass} required={interestTermsNeeded} />
                            </div>
                            <div>
                                <label className={labelClass}>Maturity Amount (₹) <span className="tw-text-[10px] tw-text-gray-400 tw-font-normal">— leave blank to auto-calculate</span></label>
                                <input type="number" min="0" step="0.01" value={form.maturity_amount} onChange={(e) => setField('maturity_amount', e.target.value)} placeholder={preview?.maturityAmount ? `Auto (₹ ${formatCurrency(preview.maturityAmount)})` : 'Auto'} className={inputClass} />
                            </div>
                        </div>

                        {/* Interest Preview */}
                        {(preview || previewLoading) && (
                            <div className="tw-border tw-border-emerald-100 tw-rounded-lg tw-overflow-hidden tw-bg-emerald-50/40">
                                <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 tw-bg-emerald-50 tw-border-b tw-border-emerald-100">
                                    {previewLoading ? (
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-emerald-700">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-emerald-300 tw-border-t-emerald-600"></div>
                                            Calculating preview...
                                        </div>
                                    ) : (
                                        <>
                                            <div className="tw-text-xs tw-text-emerald-700">
                                                Total Interest: <span className="tw-font-bold">₹{formatCurrency(preview?.totalInterest)}</span>
                                            </div>
                                            <div className="tw-text-xs tw-text-emerald-700">
                                                Maturity Amount: <span className="tw-font-bold">₹{formatCurrency(preview?.maturityAmount)}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                                {preview?.schedule && preview.schedule.length > 0 && (
                                    <div className="tw-max-h-[220px] tw-overflow-y-auto">
                                        <table className="tw-w-full tw-text-xs">
                                            <thead className="tw-sticky tw-top-0 tw-bg-white tw-z-10">
                                                <tr className="tw-border-b tw-border-emerald-100">
                                                    <th className="tw-text-left tw-px-4 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[11px]">Date</th>
                                                    <th className="tw-text-right tw-px-4 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[11px]">Amount</th>
                                                    <th className="tw-text-right tw-px-4 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[11px]">Opening Bal.</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {preview.schedule.map((row, idx) => (
                                                    <tr key={idx} className="tw-border-b tw-border-emerald-50 last:tw-border-0 hover:tw-bg-white/60">
                                                        <td className="tw-px-4 tw-py-1.5 tw-text-gray-700">{row.accrued_date}</td>
                                                        <td className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-700">₹{formatCurrency(row.accrued_amount)}</td>
                                                        <td className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-700">₹{formatCurrency(row.opening_balance)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        </>
                        )}

                        {/* Account Codes Section */}
                        <div className="tw-pt-3 tw-border-t tw-border-gray-100">
                            <h3 className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-3">Account Codes</h3>
                            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                                <div>
                                    <label className={labelClass}>
                                        Investment A/c Dr. <span className="tw-text-gray-400 tw-font-normal">(debit_account_cd)</span> {req}
                                    </label>
                                    <SearchableDropdown
                                        value={form.debit_account_cd || 'all'}
                                        onChange={(v) => setField('debit_account_cd', v === 'all' ? '' : v)}
                                        options={accountOptions}
                                        placeholder="Select account"
                                        loading={glAccountsLoading}
                                        small
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>
                                        Bank A/c Cr. <span className="tw-text-gray-400 tw-font-normal">(credit_account_cd)</span> {req}
                                    </label>
                                    <SearchableDropdown
                                        value={form.credit_account_cd || 'all'}
                                        onChange={(v) => setField('credit_account_cd', v === 'all' ? '' : v)}
                                        options={accountOptions}
                                        placeholder="Select account"
                                        loading={glAccountsLoading}
                                        small
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>
                                        Interest Income A/c <span className="tw-text-gray-400 tw-font-normal">(interest_account_cd)</span>
                                    </label>
                                    <SearchableDropdown
                                        value={form.interest_account_cd || 'all'}
                                        onChange={(v) => setField('interest_account_cd', v === 'all' ? '' : v)}
                                        options={accountOptions}
                                        placeholder="None"
                                        loading={glAccountsLoading}
                                        small
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>
                                        Interest Collection Bank <span className="tw-text-gray-400 tw-font-normal">(interest_collection_bank_cd)</span>
                                    </label>
                                    <SearchableDropdown
                                        value={form.interest_collection_bank_cd || 'all'}
                                        onChange={(v) => setField('interest_collection_bank_cd', v === 'all' ? '' : v)}
                                        options={accountOptions}
                                        placeholder="None"
                                        loading={glAccountsLoading}
                                        small
                                    />
                                </div>
                                <div className="sm:tw-col-span-2">
                                    <label className={labelClass}>
                                        Penalty A/c <span className="tw-text-gray-400 tw-font-normal">(penalty_account_cd)</span>
                                    </label>
                                    <SearchableDropdown
                                        value={form.penalty_account_cd || 'all'}
                                        onChange={(v) => setField('penalty_account_cd', v === 'all' ? '' : v)}
                                        options={accountOptions}
                                        placeholder="None"
                                        loading={glAccountsLoading}
                                        small
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Remarks */}
                        <div>
                            <label className={labelClass}>Remarks</label>
                            <textarea
                                value={form.remarks}
                                onChange={(e) => setField('remarks', e.target.value)}
                                placeholder="Optional remarks"
                                rows={2}
                                className={`${inputClass} tw-resize-none`}
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50">
                        <button
                            type="button"
                            onClick={onClose}
                            className="tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="tw-flex tw-items-center tw-gap-1 tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            {saving && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>}
                            {editId ? 'Update Investment' : 'Create Investment'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InvestmentModal;
